import {
  ChangeEvent,
  SyntheticEvent,
  FocusEvent,
  MouseEvent,
  KeyboardEvent,
  useState,
  useEffect,
  forwardRef,
  useRef,
  useImperativeHandle,
  useCallback,
  ReactNode,
} from 'react';
import cn from 'classnames';

import { ErknmFillInProcessMessage } from 'KnmErknmPackage/MainModule/components/ErknmFillInProcessMessage';
import { IInspectionResult } from 'APIPackage/CatalogsModule/interfaces/IInspectionResult';
import { InspectionResultActionTypeEnum } from 'APIPackage/CatalogsModule/enums/InspectionResultActionTypeEnum';
import { ErknmInspectionResultMessage } from 'KnmErknmPackage/MainModule/components/ErknmInspectionResultMessageMessage';
import { Hint } from 'CorePackage/MainModule/components/Hint';

import styles from './TextInput.module.scss';
import { CanBeChangedInNonWorkDaysMessage } from '../../../KnmErknmPackage/MainModule/components/CanBeChangedInNonWorkDaysMessage';
import { HadToBeHowInSignMessage } from '../../../KnmErknmPackage/MainModule/components/HadToBeHowInSignMessage';

export interface ITextInputProps {
  validateOnSubmit?: boolean;
  value?: string | number;
  className?: string;
  id?: string;
  name?: string;
  placeholder?: ReactNode;
  autoComplete?: string;
  label?: string;
  type?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  min?: string | number;
  max?: string | number;
  invalid?: boolean;
  error?: string;
  warning?: string;
  success?: string;
  tooltip?: string;
  disableErrorStyles?: boolean;
  clearable?: boolean;
  isPassword?: boolean;
  isPhone?: boolean;
  isFocused?: boolean;
  isLink?: boolean;
  withWarning?: boolean;
  isCanBeChangedInNonWorkDaysMessage?: boolean;
  hadToBeHowInSignMessage?: string | null;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onInput?: (e: SyntheticEvent) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onClick?: (e: MouseEvent<HTMLInputElement>) => void;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: KeyboardEvent) => void;
  onKeyPress?: (e: KeyboardEvent) => void;
  onClear?: () => void;
  pagination?: boolean;
  totalPages?: number;
  currentPage?: number;
  onNextPage?: () => void;
  onPrevPage?: () => void;
  inspectionResult?: IInspectionResult;
}

export const TextInput = forwardRef(
  (
    {
      value,
      className = '',
      id,
      type = 'text',
      label,
      disabled,
      invalid,
      error,
      warning,
      success,
      tooltip,
      disableErrorStyles = false,
      clearable = false,
      isPassword = false,
      isFocused = false,
      isPhone = false,
      isLink = false,
      withWarning,
      isCanBeChangedInNonWorkDaysMessage,
      hadToBeHowInSignMessage,
      onClear,
      onChange,
      onClick,
      pagination = false,
      currentPage = 0,
      totalPages = 0,
      onPrevPage,
      onNextPage,
      inspectionResult,
      placeholder,
      ...rest
    }: ITextInputProps,
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => inputRef.current);

    const [isPasswordOn, setIsPasswordOn] = useState(true);
    const classNames = cn([
      styles.InputField,
      className,
      {
        [styles.InputFieldInvalid]: invalid || Boolean(error),
        [styles.InputFieldWarning]: Boolean(warning),
        [styles.InputFieldSuccess]: Boolean(success),
        [styles.InputFieldDisabled]: disabled,
        [styles.InputFieldAsLink]: isLink,
        [styles.InputFieldDisabledErrorStyle]: disableErrorStyles,
        [styles.IsPhone]: isPhone,
        [styles.InputInspectionResultYellow]:
          inspectionResult && inspectionResult.resultActionType === InspectionResultActionTypeEnum.LIGHT_YELLOW,
        [styles.InputInspectionResultRed]:
          inspectionResult && inspectionResult.resultActionType === InspectionResultActionTypeEnum.LIGHT_RED,
      },
    ]);

    useEffect(() => {
      if (inputRef && inputRef.current) {
        isFocused && inputRef.current.focus();
      }
    }, [isFocused]);

    const togglePassword = useCallback(() => {
      if (inputRef && inputRef.current && inputRef.current.type === 'password') {
        setIsPasswordOn(false);
        inputRef.current.type = 'text';
      } else if (inputRef && inputRef.current && inputRef.current.type === 'text') {
        setIsPasswordOn(true);
        inputRef.current.type = 'password';
      }
    }, []);

    return (
      <div className={classNames}>
        {label && (
          <label className={styles.Label} htmlFor={id}>
            {label}
          </label>
        )}
        {isPhone && (
          <div className={styles.PhoneWrapper}>
            <span className={styles.Phone}>+7</span>
          </div>
        )}
        <div className={styles.ClearButtonBox} onClick={onClick}>
          <input
            className={cn(styles.Input, {
              [styles.WithWarning]: withWarning,
            })}
            id={id}
            value={value}
            ref={inputRef}
            disabled={disabled}
            type={type}
            onChange={disabled ? undefined : onChange}
            placeholder={typeof placeholder === 'string' ? placeholder : undefined}
            {...rest}
          />
          {!value && placeholder && typeof placeholder !== 'string' && (
            <div className={styles.Placeholder}>
              <div>{placeholder}</div>
            </div>
          )}
          {pagination && (
            <div className={styles.PaginationBlock}>
              <span>{`${currentPage}/${totalPages}`}</span>
              <span className={styles.TopArrowIcon} onClick={onPrevPage} />
              <span className={styles.BottomArrowIcon} onClick={onNextPage} />
            </div>
          )}
          {clearable && <button className={styles.ClearButton} type="button" onClick={onClear} hidden />}
        </div>
        {withWarning && <span className={styles.WarningIcon} />}
        {isPassword && (
          <span className={cn(styles.Mask, { [styles.MaskClose]: !isPasswordOn })} onClick={togglePassword} />
        )}
        {error && !disableErrorStyles && <div className={styles.TextInputError}>{error}</div>}
        {!error && <ErknmFillInProcessMessage name={rest.name} />}
        {!error && isCanBeChangedInNonWorkDaysMessage && (
          <CanBeChangedInNonWorkDaysMessage isActive={isCanBeChangedInNonWorkDaysMessage} />
        )}
        {!error && hadToBeHowInSignMessage && <HadToBeHowInSignMessage message={hadToBeHowInSignMessage} />}
        {warning && (
          <div className={styles.Warning}>
            <Hint text={warning} />
          </div>
        )}
        {success && <div className={styles.Success}>{success}</div>}
        {tooltip && error && (
          <div className={styles.Tooltip}>
            <div className={styles.Arrow} />
            <div className={styles.TooltipText}>{tooltip}</div>
          </div>
        )}
        {inspectionResult && <ErknmInspectionResultMessage inspectionResult={inspectionResult} />}
      </div>
    );
  },
);
